import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CloudOff, RefreshCw, TriangleAlert, Wifi, WifiOff } from 'lucide-react'
import { formatDateTime, formatNumber } from '@najah/core'
import { t } from '@najah/i18n'
import { Badge, Button, cn, useToast } from '@najah/ui'
import { getSyncStatus, syncNow } from '../lib/offline/syncEngine'
import { isTauri } from '../lib/offline/platform'
import { useOnlineStatus } from '../lib/useOnlineStatus'

const AUTO_SYNC_MS = 30_000

/**
 * حالة الاتصال والمزامنة. في المتصفّح لا يوجد طابور محلي،
 * فتُعرَض حالة الاتصال وحدها؛ في تطبيق سطح المكتب يُعرَض عدد المعلّق ويُزامَن تلقائياً.
 */
export function SyncStatus() {
  const { isConnected, serverReachable } = useOnlineStatus()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState({ pending: 0, failed: 0, lastSyncAt: null as string | null })
  const [syncing, setSyncing] = useState(false)

  const refresh = useCallback(async () => {
    if (!isTauri()) return
    try {
      const next = await getSyncStatus()
      setStatus({ pending: next.pending, failed: next.failed, lastSyncAt: next.lastSyncAt })
    } catch {
      // قاعدة محلية غير جاهزة بعد — لا شيء لنعرضه
    }
  }, [])

  const runSync = useCallback(
    async (silent = false) => {
      if (!isTauri() || syncing) return
      setSyncing(true)
      try {
        const res = await syncNow()
        await refresh()
        void queryClient.invalidateQueries({ queryKey: ['products'] })
        void queryClient.invalidateQueries({ queryKey: ['inventory'] })
        if (!silent) {
          toast.success('تمت المزامنة', `أُرسل ${formatNumber(res.pushed)} · وصل ${formatNumber(res.pulled)}`)
        }
      } catch {
        if (!silent) toast.error(t.sync.syncFailed, 'سنُعيد المحاولة تلقائياً')
      } finally {
        setSyncing(false)
      }
    },
    [syncing, refresh, queryClient, toast],
  )

  useEffect(() => {
    void refresh()
  }, [refresh])

  // مزامنة دورية صامتة، وفوراً عند عودة الاتصال
  useEffect(() => {
    if (!isTauri() || !isConnected) return
    void runSync(true)
    const id = setInterval(() => void runSync(true), AUTO_SYNC_MS)
    return () => clearInterval(id)
    // runSync يتغيّر مع كل تبديل لـ syncing؛ نعتمد على isConnected وحده لإعادة الجدولة
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected])

  if (serverReachable === null && !isTauri()) return null

  const hasQueue = status.pending > 0 || status.failed > 0

  return (
    <div className="flex items-center gap-2">
      {status.failed > 0 && (
        <Badge tone="danger" dot title="عمليات رفضها الخادم — تحتاج مراجعة">
          <TriangleAlert className="size-3.5" />
          {formatNumber(status.failed)} مرفوضة
        </Badge>
      )}

      <Badge
        tone={!isConnected ? 'warning' : hasQueue ? 'info' : 'success'}
        dot
        title={
          status.lastSyncAt
            ? `${t.sync.lastSync}: ${formatDateTime(status.lastSyncAt)}`
            : isConnected
              ? undefined
              : t.sync.offlineNotice
        }
      >
        {!isConnected ? <WifiOff className="size-3.5" /> : hasQueue ? <CloudOff className="size-3.5" /> : <Wifi className="size-3.5" />}
        {!isConnected
          ? t.sync.offline
          : status.pending > 0
            ? t.sync.pending(status.pending)
            : t.sync.online}
      </Badge>

      {isTauri() && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => void runSync(false)}
          disabled={syncing || !isConnected}
          title={t.sync.syncNow}
          aria-label={t.sync.syncNow}
        >
          <RefreshCw className={cn('size-4', syncing && 'animate-spin')} />
        </Button>
      )}
    </div>
  )
}
